import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Fab,
  MenuItem,
  Menu,
  Chip,
  Autocomplete,
  Popper,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Business,
  Add,
  Search,
  Edit,
  Delete,
  Language,
  AutoAwesome,
  MoreVert,
  ViewList,
  ViewModule,
  CalendarToday,
  Clear,
  FilterList,
  History,
  TrendingUp,
  Public,
  Store,
} from "@mui/icons-material";
import { PageHeader, LoadingSpinner } from "../components";
import { supabaseApi } from "../services/supabaseApi";
import { Merchant, MerchantFormData } from "@catalog/types";
import { useDebounce } from "../hooks/useDebounce";

// Search utilities
interface SearchResult {
  merchant: Merchant;
  score: number;
  matches: {
    field: string;
    text: string;
    highlighted: string;
  }[];
}

interface SearchFilter {
  id: string;
  label: string;
  icon: React.ReactElement;
  filter: (merchant: Merchant) => boolean;
  count?: number;
}

// Fuzzy search function with scoring
const fuzzySearch = (
  searchTerm: string,
  merchants: Merchant[]
): SearchResult[] => {
  if (!searchTerm.trim()) {
    return merchants.map((merchant) => ({
      merchant,
      score: 1,
      matches: [],
    }));
  }

  const term = searchTerm.toLowerCase().trim();
  const results: SearchResult[] = [];

  merchants.forEach((merchant) => {
    const matches: SearchResult["matches"] = [];
    let score = 0;

    // Search in name (highest weight)
    const nameMatch = merchant.name.toLowerCase();
    if (nameMatch.includes(term)) {
      const exactMatch = nameMatch === term;
      const startsWith = nameMatch.startsWith(term);
      score += exactMatch ? 100 : startsWith ? 80 : 60;

      matches.push({
        field: "name",
        text: merchant.name,
        highlighted: highlightText(merchant.name, term),
      });
    }

    // Search in URL (medium weight)
    if (merchant.url) {
      const urlMatch = merchant.url.toLowerCase();
      if (urlMatch.includes(term)) {
        score += 40;
        matches.push({
          field: "url",
          text: merchant.url,
          highlighted: highlightText(merchant.url, term),
        });
      }
    }

    // Search in website (medium weight)
    if (merchant.website) {
      const websiteMatch = merchant.website.toLowerCase();
      if (websiteMatch.includes(term)) {
        score += 35;
        matches.push({
          field: "website",
          text: merchant.website,
          highlighted: highlightText(merchant.website, term),
        });
      }
    }

    // Partial word matching (lower weight)
    const words = term.split(" ").filter((w) => w.length > 1);
    words.forEach((word) => {
      if (nameMatch.includes(word) && !nameMatch.includes(term)) {
        score += 20;
      }
    });

    // Add to results if any matches found
    if (score > 0) {
      results.push({ merchant, score, matches });
    }
  });

  // Sort by score (descending)
  return results.sort((a, b) => b.score - a.score);
};

// Highlight matching text
const highlightText = (text: string, searchTerm: string): string => {
  if (!searchTerm.trim()) return text;

  const regex = new RegExp(
    `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi"
  );
  return text.replace(
    regex,
    '<mark style="background-color: #fff3cd; padding: 1px 2px; border-radius: 2px;">$1</mark>'
  );
};

// Search history management
const SEARCH_HISTORY_KEY = "merchants_search_history";
const MAX_HISTORY_ITEMS = 10;

const getSearchHistory = (): string[] => {
  try {
    const history = localStorage.getItem(SEARCH_HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch {
    return [];
  }
};

const addToSearchHistory = (searchTerm: string) => {
  if (!searchTerm.trim()) return;

  try {
    const history = getSearchHistory();
    const filtered = history.filter((item) => item !== searchTerm);
    const newHistory = [searchTerm, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
  } catch {
    // Ignore localStorage errors
  }
};

interface MerchantFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: MerchantFormData) => void;
  initialData?: Merchant;
  mode?: "create" | "edit";
}

const MerchantForm: React.FC<MerchantFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  mode = "create",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState<MerchantFormData>({
    name: initialData?.name || "",
    url: initialData?.url || "",
    website: initialData?.website || "",
    image_url: initialData?.image_url || "",
    is_active: initialData?.is_active ?? true,
  });

  const [isAutoFetching, setIsAutoFetching] = useState(false);

  // Reset form data when dialog opens or when switching between create/edit modes
  useEffect(() => {
    if (open) {
      setFormData({
        name: initialData?.name || "",
        url: initialData?.url || "",
        website: initialData?.website || "",
        image_url: initialData?.image_url || "",
        is_active: initialData?.is_active ?? true,
      });
    }
  }, [open, initialData]);

  const handleInputChange = (field: keyof MerchantFormData, value: any) => {
    setFormData((prev: MerchantFormData) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAutoFetch = async () => {
    if (!formData.url) return;

    setIsAutoFetching(true);
    try {
      // Extract domain name for merchant name if not provided
      const urlObj = new URL(
        formData.url.startsWith("http")
          ? formData.url
          : `https://${formData.url}`
      );
      const domain = urlObj.hostname.replace("www.", "");
      const merchantName = domain.split(".")[0].toUpperCase();

      // Auto-populate basic information
      setFormData((prev) => ({
        ...prev,
        name: prev.name || merchantName,
        website: urlObj.href,
        // For now, we'll use a placeholder image. In a real implementation,
        // you could use a service like Clearbit Logo API or similar
        image_url: prev.image_url || `https://logo.clearbit.com/${domain}`,
      }));

      // TODO: In a real implementation, you could:
      // 1. Use a web scraping service to get merchant info
      // 2. Use business APIs like Clearbit, FullContact, etc.
      // 3. Use OpenGraph meta tags from the website
    } catch (error) {
      console.error("Error auto-fetching merchant info:", error);
    } finally {
      setIsAutoFetching(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile} // Full screen on mobile
    >
      <DialogTitle
        sx={{
          pb: { xs: 1, md: 2 }, // Reduced padding on mobile
          px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
        }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Business sx={{ fontSize: { xs: 20, md: 24 } }} />{" "}
          {/* Smaller icon on mobile */}
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.125rem", md: "1.25rem" } }} // Smaller title on mobile
          >
            {mode === "create" ? "Add New Merchant" : "Edit Merchant"}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
          pt: { xs: 1, md: 2 }, // Reduced top padding on mobile
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ pt: { xs: 1, md: 2 } }}
        >
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {" "}
            {/* Reduced spacing on mobile */}
            {/* URL Field with Auto-Fetch */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Merchant URL"
                value={formData.url}
                onChange={(e) => handleInputChange("url", e.target.value)}
                placeholder="https://www.ikea.com or ikea.com"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleAutoFetch}
                        disabled={!formData.url || isAutoFetching}
                        color="primary"
                        title="Auto-fetch merchant info"
                      >
                        {isAutoFetching ? (
                          <CircularProgress size={20} />
                        ) : (
                          <AutoAwesome />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Enter the merchant's website URL and click the magic icon to auto-fetch info"
              />
            </Grid>
            {/* Name Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Merchant Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                placeholder="e.g., IKEA, Amazon, Best Buy"
              />
            </Grid>
            {/* Website Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                placeholder="https://www.merchant.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Language />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* Image URL Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL"
                value={formData.image_url}
                onChange={(e) => handleInputChange("image_url", e.target.value)}
                placeholder="https://example.com/logo.png"
                helperText="Optional: URL to the merchant's logo image"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 2, md: 3 }, // Reduced padding on mobile
          gap: { xs: 1, md: 0 }, // Add gap on mobile
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
            px: { xs: 2, md: 3 }, // Reduced padding on mobile
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim()}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
            px: { xs: 2, md: 3 }, // Reduced padding on mobile
          }}
        >
          {mode === "create" ? "Add Merchant" : "Update Merchant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  merchant: Merchant | null;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  merchant,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile} // Full screen on mobile
    >
      <DialogTitle sx={{ pb: { xs: 1, md: 2 }, px: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Avatar
            sx={{
              bgcolor: "error.main",
              width: { xs: 36, md: 40 },
              height: { xs: 36, md: 40 },
            }}
          >
            <Delete sx={{ fontSize: { xs: 18, md: 20 } }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.125rem", md: "1.25rem" } }}
          >
            Delete Merchant
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, md: 3 }, pt: { xs: 1, md: 2 } }}>
        <Stack spacing={{ xs: 1.5, md: 2 }}>
          {merchant?.image_url && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <Avatar
                src={merchant.image_url}
                sx={{
                  width: { xs: 40, md: 48 },
                  height: { xs: 40, md: 48 },
                  borderRadius: 1,
                }}
              >
                {merchant.name?.[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}
                >
                  {merchant.name}
                </Typography>
                {merchant.url && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                  >
                    {merchant.url.replace(/^https?:\/\//, "")}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          <Typography sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
            Are you sure you want to delete this merchant? This action cannot be
            undone.
          </Typography>
          <Alert
            severity="warning"
            sx={{
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              "& .MuiAlert-icon": {
                fontSize: { xs: 18, md: 22 },
              },
            }}
          >
            Any items associated with this merchant will remain but lose their
            merchant reference.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          pb: { xs: 2, md: 3 },
          gap: { xs: 1, md: 0 },
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Delete Merchant
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface MerchantCardProps {
  merchant: Merchant;
  onEdit: () => void;
  onDelete: () => void;
  searchMatches?: SearchResult["matches"];
}

const MerchantCard: React.FC<MerchantCardProps> = ({
  merchant,
  onEdit,
  onDelete,
  searchMatches = [],
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit();
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete();
    handleMenuClose();
  };

  // Get highlighted text for a field
  const getHighlightedText = (field: string, fallback: string) => {
    const match = searchMatches.find((m) => m.field === field);
    return match ? match.highlighted : fallback;
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: { xs: "none", sm: "translateY(-2px)" }, // Disable hover animation on mobile
          boxShadow: {
            xs: "0 2px 8px rgba(0, 0, 0, 0.08)",
            sm: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {" "}
        {/* Reduced mobile padding */}
        <Stack spacing={{ xs: 1.5, md: 2 }}>
          {" "}
          {/* Reduced mobile spacing */}
          {/* Header with Logo and Menu */}
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={{ xs: 1.5, md: 2 }}
          >
            <Avatar
              src={merchant.image_url}
              sx={{
                width: { xs: 40, md: 56 }, // Smaller avatar on mobile
                height: { xs: 40, md: 56 },
                backgroundColor: merchant.color || "primary.main",
                borderRadius: 1,
              }}
            >
              {merchant.name?.[0]?.toUpperCase() || (
                <Business sx={{ fontSize: { xs: 16, md: 20 } }} />
              )}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  mb: 0.5,
                  fontSize: { xs: "1rem", md: "1.25rem" }, // Smaller text on mobile
                }}
                dangerouslySetInnerHTML={{
                  __html: getHighlightedText("name", merchant.name),
                }}
              />
              {merchant.website && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    display: "block",
                    fontSize: { xs: "0.65rem", md: "0.75rem" }, // Smaller text on mobile
                  }}
                  onClick={() => window.open(merchant.website, "_blank")}
                >
                  Visit Store
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                color: "text.secondary",
                padding: { xs: 0.5, md: 1 }, // Smaller touch target on mobile
              }}
            >
              <MoreVert sx={{ fontSize: { xs: 18, md: 20 } }} />
            </IconButton>
          </Stack>
          {/* Website Link */}
          {merchant.url && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Language
                sx={{ fontSize: { xs: 14, md: 16 }, color: "text.secondary" }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.75rem", md: "0.875rem" }, // Smaller text on mobile
                }}
                dangerouslySetInnerHTML={{
                  __html: getHighlightedText(
                    "url",
                    merchant.url.replace(/^https?:\/\//, "")
                  ),
                }}
              />
            </Stack>
          )}
          {/* Created Date */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarToday
              sx={{ fontSize: { xs: 14, md: 16 }, color: "text.secondary" }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }} // Smaller text on mobile
            >
              Added{" "}
              {new Date(merchant.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: isMobile ? "2-digit" : "numeric", // Shorter date format on mobile
              })}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 2, fontSize: { xs: 18, md: 20 } }} />
          <Typography sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
            Edit
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <Delete sx={{ mr: 2, fontSize: { xs: 18, md: 20 } }} />
          <Typography sx={{ fontSize: { xs: "0.875rem", md: "1rem" } }}>
            Delete
          </Typography>
        </MenuItem>
      </Menu>
    </Card>
  );
};

const MerchantsPage: React.FC = () => {
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState<
    "name" | "created" | "url" | "relevance"
  >("name");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState<Merchant | null>(null);
  const [merchantToDelete, setMerchantToDelete] = useState<Merchant | null>(
    null
  );
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const queryClient = useQueryClient();
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const { data: merchants = [], isLoading } = useQuery({
    queryKey: ["merchants"],
    queryFn: supabaseApi.merchants.getAll,
  });

  // Load search history on mount
  useEffect(() => {
    setSearchHistory(getSearchHistory());
  }, []);

  // Add to search history when search is performed
  useEffect(() => {
    if (debouncedSearchValue.trim()) {
      addToSearchHistory(debouncedSearchValue);
      setSearchHistory(getSearchHistory());
    }
  }, [debouncedSearchValue]);

  // Create search filters
  const searchFilters: SearchFilter[] = useMemo(() => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const filters: SearchFilter[] = [
      {
        id: "recent",
        label: "Recent",
        icon: <TrendingUp />,
        filter: (merchant) => new Date(merchant.created_at) > oneWeekAgo,
      },
      {
        id: "this-month",
        label: "This Month",
        icon: <CalendarToday />,
        filter: (merchant) => new Date(merchant.created_at) > oneMonthAgo,
      },
      {
        id: "has-website",
        label: "Has Website",
        icon: <Public />,
        filter: (merchant) => !!merchant.website,
      },
      {
        id: "has-logo",
        label: "Has Logo",
        icon: <Store />,
        filter: (merchant) => !!merchant.image_url,
      },
    ];

    // Add counts to filters
    return filters.map((filter) => ({
      ...filter,
      count: merchants.filter(filter.filter).length,
    }));
  }, [merchants]);

  // Perform search and filtering
  const searchResults = useMemo(() => {
    let results = fuzzySearch(debouncedSearchValue, merchants);

    // Apply active filters
    if (activeFilters.length > 0) {
      const activeFilterFunctions = searchFilters
        .filter((f) => activeFilters.includes(f.id))
        .map((f) => f.filter);

      results = results.filter((result) =>
        activeFilterFunctions.every((filterFn) => filterFn(result.merchant))
      );
    }

    // Sort results
    if (sortBy === "relevance" && debouncedSearchValue.trim()) {
      // Already sorted by relevance score
      return results;
    } else {
      const sorted = [...results].sort((a, b) => {
        switch (sortBy) {
          case "name":
            return a.merchant.name.localeCompare(b.merchant.name);
          case "created":
            return (
              new Date(b.merchant.created_at).getTime() -
              new Date(a.merchant.created_at).getTime()
            );
          case "url":
            return (a.merchant.url || "").localeCompare(b.merchant.url || "");
          default:
            return 0;
        }
      });
      return sorted;
    }
  }, [merchants, debouncedSearchValue, sortBy, activeFilters, searchFilters]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleSearchSelect = (value: string | null) => {
    if (value) {
      setSearchValue(value);
    }
  };

  const handleClearSearch = () => {
    setSearchValue("");
  };

  const handleFilterToggle = (filterId: string) => {
    setActiveFilters((prev) =>
      prev.includes(filterId)
        ? prev.filter((id) => id !== filterId)
        : [...prev, filterId]
    );
  };

  const handleAddMerchant = () => {
    setEditingMerchant(null);
    setIsFormOpen(true);
  };

  const handleEditMerchant = (merchant: Merchant) => {
    setEditingMerchant(merchant);
    setIsFormOpen(true);
  };

  const handleDeleteMerchant = (merchant: Merchant) => {
    setMerchantToDelete(merchant);
    setIsDeleteDialogOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingMerchant(null);
  };

  const handleFormSubmit = async (formData: MerchantFormData) => {
    try {
      if (editingMerchant) {
        await supabaseApi.merchants.update(editingMerchant.id, formData);
        setSnackbar({
          open: true,
          message: "Merchant updated successfully!",
          severity: "success",
        });
      } else {
        await supabaseApi.merchants.create(formData);
        setSnackbar({
          open: true,
          message: "Merchant added successfully!",
          severity: "success",
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["merchants"] });
      handleFormClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to save merchant. Please try again.",
        severity: "error",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!merchantToDelete) return;

    try {
      await supabaseApi.merchants.delete(merchantToDelete.id);
      setSnackbar({
        open: true,
        message: "Merchant deleted successfully!",
        severity: "success",
      });
      await queryClient.invalidateQueries({ queryKey: ["merchants"] });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete merchant. Please try again.",
        severity: "error",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setMerchantToDelete(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading merchants..." />;
  }

  const hasActiveSearch =
    debouncedSearchValue.trim() || activeFilters.length > 0;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: "text.primary",
                  letterSpacing: "-0.02em",
                  fontSize: { xs: "1.5rem", md: "2.125rem" },
                }}
              >
                Merchants
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 400 }}
              >
                Manage your favorite online stores and merchants
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddMerchant}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                display: { xs: "none", sm: "flex" },
              }}
            >
              Add Merchant
            </Button>
          </Stack>
        </Box>

        {/* Search and Controls */}
        <Paper
          sx={{
            p: { xs: 1.5, md: 3 },
            mb: { xs: 1.5, md: 3 },
            borderRadius: 2,
          }}
        >
          <Stack spacing={{ xs: 2, md: 3 }}>
            {/* Search Input */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
            >
              <Autocomplete
                freeSolo
                options={searchHistory}
                value={searchValue}
                onInputChange={(event, newValue) => {
                  setSearchValue(newValue || "");
                }}
                onChange={(event, newValue) => {
                  handleSearchSelect(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search merchants..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: searchValue && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <ListItemIcon>
                      <History sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary={option} />
                  </Box>
                )}
                sx={{ minWidth: { sm: 300 }, flex: 1 }}
              />

              <Stack direction="row" spacing={1}>
                <Tooltip title="Quick Filters">
                  <IconButton
                    onClick={() => setShowFilters(!showFilters)}
                    color={activeFilters.length > 0 ? "primary" : "default"}
                  >
                    <Badge badgeContent={activeFilters.length} color="primary">
                      <FilterList />
                    </Badge>
                  </IconButton>
                </Tooltip>

                <TextField
                  select
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="url">URL</MenuItem>
                  {debouncedSearchValue.trim() && (
                    <MenuItem value="relevance">Relevance</MenuItem>
                  )}
                </TextField>

                <IconButton
                  onClick={() =>
                    setViewMode(viewMode === "grid" ? "list" : "grid")
                  }
                  sx={{ ml: 1 }}
                >
                  {viewMode === "grid" ? <ViewList /> : <ViewModule />}
                </IconButton>
              </Stack>
            </Stack>

            {/* Quick Filters */}
            {showFilters && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                  >
                    Quick Filters
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {searchFilters.map((filter) => (
                      <Chip
                        key={filter.id}
                        icon={filter.icon}
                        label={`${filter.label} (${filter.count})`}
                        variant={
                          activeFilters.includes(filter.id)
                            ? "filled"
                            : "outlined"
                        }
                        onClick={() => handleFilterToggle(filter.id)}
                        size="small"
                        color={
                          activeFilters.includes(filter.id)
                            ? "primary"
                            : "default"
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </Paper>

        {/* Merchants Grid */}
        {searchResults.length === 0 ? (
          <Paper
            sx={{ p: { xs: 3, md: 6 }, textAlign: "center", borderRadius: 2 }}
          >
            <Store
              sx={{
                fontSize: { xs: 48, md: 64 },
                color: "text.disabled",
                mb: 2,
              }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {hasActiveSearch ? "No merchants found" : "No merchants yet"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {hasActiveSearch
                ? "Try adjusting your search or filters"
                : "Add your first merchant to start tracking your favorite stores"}
            </Typography>
            {!hasActiveSearch && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddMerchant}
                sx={{
                  fontSize: { xs: "0.8125rem", md: "0.875rem" },
                  py: { xs: 0.75, md: 1 },
                }}
              >
                Add Merchant
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {searchResults.map(({ merchant, matches }) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={merchant.id}>
                <MerchantCard
                  merchant={merchant}
                  onEdit={() => handleEditMerchant(merchant)}
                  onDelete={() => handleDeleteMerchant(merchant)}
                  searchMatches={matches}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Floating Action Button for Mobile */}
        <Fab
          color="primary"
          onClick={handleAddMerchant}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            display: { xs: "flex", sm: "none" },
          }}
        >
          <Add />
        </Fab>

        {/* Merchant Form Dialog */}
        <MerchantForm
          open={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={editingMerchant || undefined}
          mode={editingMerchant ? "edit" : "create"}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          merchant={merchantToDelete}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default MerchantsPage;
